import {Grid, MenuItem} from '@material-ui/core'
import {DropzoneArea} from 'material-ui-dropzone'
import {useState} from 'react'
import {contentURL} from 'src/api/reactpt'
import Action from 'src/models/Action'
import Activity from 'src/models/Activity'
import Goal from 'src/models/Goal'
import Routine from 'src/models/Routine'
import Autocomplete, {DataType} from '../Autocomplete'
import ButtonSmall from '../Buttons/ButtonSmall'
import EditTemplate from '../EditTemplate'
import TextInput from '../TextInput'
import Subtitle1 from '../Typography/Subtitle1'
import ActionsDialog from './ActionsDialog'
import ActionsTable from './ActionsTable'
import {FormContainer, Root, Thumbnail, InnerContainer} from './styled'

const yesNoOptions = [
  {value: 1, label: 'Yes'},
  {value: 0, label: 'No'},
]

const publishOptions = [
  {value: 1, label: 'Yes - This Routine is available to users'},
  {value: 0, label: 'No - Unavailable to users'},
]

export type RoutineFormProps = {
  editRoutine?: Routine
  title: string
  allGoals: Goal[]
  allActivities: Activity[]
  allActions: Action[]
  successString?: string
  busyString?: string
  errorMessage?: string
  onSaveRoutine: (updatedRoutine: Routine, imageFile: File | undefined) => void
  onCancel: () => void
  onDismissSuccess: () => void
}

const blankRoutine: Routine = {
  id: undefined,
  title: '',
  description: '',
  instructions: '',
  expectedTime: 1,
  imageContentName: '',
  actions: [],
  goal: undefined,
  activity: undefined,
  isFeatured: false,
  isPublished: false,
  imageType: 'Hero',
}

const RoutineForm: React.FC<RoutineFormProps> = ({
  editRoutine,
  title,
  allGoals,
  allActivities,
  allActions,
  successString,
  busyString,
  errorMessage,
  onSaveRoutine,
  onCancel,
  onDismissSuccess,
}: RoutineFormProps) => {
  const [routine, setRoutine] = useState<Routine>(editRoutine ? editRoutine : blankRoutine)
  const [showActionsDialog, setShowActionsDialog] = useState<boolean>(false)
  const [imageFile, setImageFile] = useState<File | undefined>()

  ///
  /// Called when the save button is clicked.
  ///
  const onSaveClick = () => {
    // Need to save this routine.
    onSaveRoutine(routine, imageFile)
  }

  ///
  /// Called when the use closes the "select actions" dialog box.
  ///
  const onSelectActions = (selectedActions: Action[] | undefined) => {
    if (selectedActions) {
      // The user has selected some actions - so save them!
      setRoutine({...routine, actions: selectedActions})
    }

    // Close the dialog.
    setShowActionsDialog(false)
  }

  ///
  /// This converts our "goal" into a "datatype" for the auto-complete component. We do this because a
  /// goal has both a full-name and a short-name, and in this case we want to use the full name.
  ///
  const goalForAutoComplete = (goal: Goal): Goal & DataType => {
    return {...goal, name: goal.nameFull}
  }

  ///
  /// This is called when the user has re-ordered an action. We stash the new list in the routine!
  ///
  const onActionsUpdated = (updatedActions: Action[]) => {
    setRoutine({...routine, actions: updatedActions})
  }

  const actionsAddRemoveText = routine.actions.length === 0 ? 'Add Actions' : 'Add/Remove Actions'

  return (
    <Root>
      <EditTemplate
        title={title}
        busyString={busyString}
        errorMessage={errorMessage}
        successString={successString}
        allFieldsRequired
        onSave={onSaveClick}
        onCancel={onCancel}
        onDismissSuccess={onDismissSuccess}>
        <FormContainer border={1} borderColor="text.secondary" borderRadius={10}>
          <Grid container spacing={5} xs={12} alignItems="center" justify="center">
            <Grid item xs={8}>
              <TextInput
                fullWidth={true}
                id="title"
                label="Routine Title"
                value={routine.title}
                onChangeText={(newTitle: string) => {
                  setRoutine({...routine, title: newTitle})
                }}
                onSubmitEditing={onSaveClick}
              />
            </Grid>
            <Grid item xs={8}>
              <TextInput
                fullWidth={true}
                id="description"
                label="Description"
                value={routine.description}
                onChangeText={(description: string) => {
                  setRoutine({...routine, description})
                }}
                onSubmitEditing={onSaveClick}
              />
            </Grid>
            <Grid item xs={8}>
              <TextInput
                select
                value={routine.isFeatured ? 1 : 0}
                fullWidth={true}
                id="isFeatured"
                label="Is this a featured routine?"
                onChangeText={(text: string) => {
                  setRoutine({...routine, isFeatured: JSON.stringify(text) === '1'})
                }}
                onSubmitEditing={onSaveClick}>
                {yesNoOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextInput>
            </Grid>
            <Grid item xs={8}>
              <TextInput
                fullWidth={true}
                multiline
                id="instructions"
                label="Instructions"
                value={routine.instructions}
                onChangeText={(instructions: string) => {
                  setRoutine({...routine, instructions})
                }}
              />
            </Grid>
            <Grid item xs={8}>
              <TextInput
                fullWidth={true}
                type="number"
                id="time"
                label="Estimate Time (minutes)"
                value={routine.expectedTime}
                onChangeText={(expectedTimeString: string) => {
                  setRoutine({...routine, expectedTime: Number(expectedTimeString)})
                }}
                onSubmitEditing={onSaveClick}
              />
            </Grid>
            <Grid item xs={8}>
              <InnerContainer border={1} borderColor="text.secondary" borderRadius={3}>
                <Grid container spacing={5} xs={12} alignItems="flex-start">
                  <Grid item xs={12}>
                    <Subtitle1>Image:</Subtitle1>
                  </Grid>
                  <Grid item xs={3} />
                  <Grid item xs={3}>
                    <Thumbnail variant="square" src={contentURL(routine.imageContentName)} />
                  </Grid>
                  <Grid item xs={3}>
                    <DropzoneArea
                      showPreviewsInDropzone={true}
                      showFileNames={true}
                      showPreviews={false}
                      acceptedFiles={['image/png', 'image/jpeg']}
                      maxFileSize={1073741824} // one gigabyte
                      filesLimit={1}
                      onChange={(selected) => {
                        setImageFile(selected.shift())
                      }}
                    />
                  </Grid>
                  <Grid item xs={3} />
                </Grid>
              </InnerContainer>
            </Grid>
            <Grid item xs={5}>
              <Autocomplete
                id="activities"
                title="Activities"
                placeholderText="Select an activity"
                value={routine.activity}
                data={allActivities}
                onChange={(newActivity) =>
                  setRoutine({...routine, activity: newActivity as Activity})
                }
              />
            </Grid>
            <Grid item xs={5}>
              <Autocomplete
                id="goals"
                title="Goal"
                placeholderText="Select a goal"
                value={routine.goal ? goalForAutoComplete(routine.goal) : undefined}
                data={allGoals.map((goal) => goalForAutoComplete(goal))}
                onChange={(newGoal) => setRoutine({...routine, goal: newGoal as unknown as Goal})}
              />
            </Grid>
            <Grid item xs={8}>
              <InnerContainer border={1} borderColor="text.secondary" borderRadius={3}>
                <Grid container xs={12} alignItems="flex-start">
                  <Grid item xs={6}>
                    <Subtitle1>Actions:</Subtitle1>
                  </Grid>
                  <Grid item xs={6}>
                    <ButtonSmall
                      onClick={() => {
                        setShowActionsDialog(true)
                      }}>
                      {actionsAddRemoveText}
                    </ButtonSmall>
                  </Grid>
                  <Grid item xs={12}>
                    <ActionsTable
                      orderedActions={routine.actions}
                      onActionsUpdated={onActionsUpdated}
                    />
                  </Grid>
                </Grid>
              </InnerContainer>
            </Grid>
            <Grid item xs={8}>
              <TextInput
                select
                value={routine.isPublished ? 1 : 0}
                fullWidth={true}
                id="isPublished"
                label="Is this routine published?"
                onChangeText={(text: string) => {
                  setRoutine({...routine, isPublished: JSON.stringify(text) === '1'})
                }}
                onSubmitEditing={onSaveClick}>
                {publishOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextInput>
            </Grid>
          </Grid>
        </FormContainer>
        <ActionsDialog
          open={showActionsDialog}
          allActions={allActions}
          selectedActions={routine.actions}
          onClose={(selectedActions) => {
            onSelectActions(selectedActions)
          }}
        />
      </EditTemplate>
    </Root>
  )
}

export default RoutineForm
